import { useEffect, useMemo, useState } from 'react';

export interface DesktopLatest {
  version: string;
  releasedAt: string;
  releasesUrl: string;
  mac: string | null;
  windows: string | null;
  windowsMsi: string | null;
  windowsExe: string | null;
}

export type DesktopOS = 'mac' | 'windows' | 'other';

const API_BASE = import.meta.env.DEV ? '/api' : 'https://api.pollenate.dev';

export function detectOS(): DesktopOS {
  if (typeof navigator === 'undefined') return 'other';
  const ua = navigator.userAgent.toLowerCase();
  if (ua.includes('mac')) return 'mac';
  if (ua.includes('win')) return 'windows';
  return 'other';
}

/**
 * Desktop-download state for the /download page. Detects the visitor's OS
 * and fetches the latest signed build metadata from `GET /desktop/latest`
 * (served by the worker from the R2 release manifest). A `version` of
 * "0.0.0" means no build has been published yet.
 */
export function useDesktopLatest() {
  const os = useMemo(detectOS, []);
  const [latest, setLatest] = useState<DesktopLatest | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    fetch(`${API_BASE}/desktop/latest`)
      .then((r) => (r.ok ? (r.json() as Promise<DesktopLatest>) : null))
      .then((data) => {
        if (active) setLatest(data && data.version !== '0.0.0' ? data : null);
      })
      .catch(() => {})
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  return {
    os,
    latest,
    loading,
    macHref: latest?.mac ?? null,
    winMsiHref: latest?.windowsMsi ?? null,
    winExeHref: latest?.windowsExe ?? null,
  };
}
